import type { Appearance } from '@stripe/stripe-js';
import { tokens } from '@pie/amber-tokens/insured/tokens';

/**
 * Convert an `rgb()/rgba()` string to hex. Amber tokens are rgba strings;
 * Stripe's color-derivation engine (e.g. invalid-input shades) doesn't parse
 * rgba reliably and falls back to the theme default, so we hand it hex.
 */
const toHex = (value: string): string => {
  const match = value.match(/^rgba?\(([^)]+)\)$/);
  if (!match) return value;
  const [r, g, b, a = 1] = match[1].split(',').map((part) => Number(part.trim()));
  const channel = (n: number) => Math.round(n).toString(16).padStart(2, '0');
  const hex = `#${channel(r)}${channel(g)}${channel(b)}`;
  return a < 1 ? `${hex}${channel(a * 255)}` : hex;
};

/**
 * Resolve an Amber design token to a concrete hex value. The Payment Element
 * iframe (different origin) can't read our CSS variables, so we bake concrete
 * values into the Appearance config. Throws on missing tokens so renames in
 * `@pie/amber-tokens` are caught at first render.
 */
const amber = (name: string): string => {
  const found = tokens.find((token) => token.name === name);
  if (!found) {
    throw new Error(`Amber token "${name}" not found — has it been renamed?`);
  }
  if (typeof found.$value !== 'string') {
    throw new Error(`Amber token "${name}" has a non-string value`);
  }
  return toHex(found.$value);
};

const colors = {
  accent: amber('color-border-action-accent-heavy-default'),
  accentSoftBg: amber('color-bg-layout-container-selected'),
  surface: amber('color-bg-layout-default-default'),
  text: amber('color-text-heading-default'),
  textSubtle: amber('color-text-subtle-default'),
  danger: amber('color-text-negative-default'),
  borderNeutral: amber('color-border-action-neutral-medium-default'),
};

// See https://docs.stripe.com/elements/appearance-api
export const stripeAppearance: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: colors.accent,
    colorBackground: colors.surface,
    colorText: colors.text,
    colorTextSecondary: colors.textSubtle,
    colorDanger: colors.danger,
    fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
    fontSizeBase: '14px',
    spacingUnit: '4px',
    borderRadius: '4px',
    colorIcon: colors.text,
    colorIconTabSelected: colors.accent,
  },
  rules: {
    '.Label': {
      fontWeight: '500',
      color: colors.text,
      marginBottom: '4px',
    },
    '.Input': {
      border: `1px solid ${colors.borderNeutral}`,
      boxShadow: 'none',
      padding: '10px 12px',
      width: '100%',
    },
    '.Block': {
      border: 'none',
      padding: '0',
      width: '100%',
    },
    '.AccordionItem': {
      width: '100%',
    },
    '.Input:focus': {
      borderColor: colors.accent,
      // Inset shadow so the focus ring isn't clipped by the iframe's overflow:hidden.
      boxShadow: `inset 0 0 0 1px ${colors.accent}`,
    },
    '.Input--invalid': {
      borderColor: colors.danger,
      boxShadow: 'none',
    },
    '.Tab': {
      // Inset box-shadow (not border) so the highlight stays in bounds —
      // overflow:hidden was clipping the outer Tabs' edges.
      border: 'none',
      boxShadow: `inset 0 0 0 1px ${colors.borderNeutral}`,
      padding: '12px 16px',
      borderRadius: '8px',
      flexGrow: '1',
      flexBasis: '0',
    },
    '.Tab:hover': {
      boxShadow: `inset 0 0 0 1px ${colors.accent}`,
      color: colors.accent,
    },
    '.Tab--selected': {
      boxShadow: `inset 0 0 0 1px ${colors.accent}`,
      backgroundColor: colors.accentSoftBg,
      color: colors.accent,
    },
    '.Tab--selected:focus': {
      boxShadow: `inset 0 0 0 1px ${colors.accent}`,
    },
    '.TabLabel': {
      fontWeight: '500',
    },
    '.Error': {
      color: colors.danger,
    },
  },
};
